import React, { useState, useRef } from 'react';
import {
  FormatBold,
  FormatItalic,
  Code,
  Link,
  Title,
} from '@mui/icons-material';
import {
  IconButton,
  Popover,
  Tooltip,
  Divider,
} from '@mui/material';
import styles from './RichTextEditor.module.scss';

const HEADING_LEVELS = [1, 2, 3, 4, 5, 6];

/**
 * BubbleToolbar
 *
 * Props:
 *   editor          – Tiptap editor instance
 *   onOpenLinkDialog– () => void  called when the Link button is clicked;
 *                     the parent (RichTextEditor) owns the link Dialog so it
 *                     renders as a body-level portal, fully independent of the
 *                     editor's focus/blur lifecycle.
 *   onEngaged       – (boolean) → called when the Heading picker opens/closes
 *                     so the parent can block the blur-hide timer.
 */
export default function BubbleToolbar({ editor, onOpenLinkDialog, onEngaged }) {
  const [headingOpen, setHeadingOpen] = useState(false);
  const headingAnchor = useRef(null);

  if (!editor) return null;

  // ── Bold / Italic / Inline Code ─────────────────────────────────────────
  const handleBold   = () => editor.chain().focus().toggleBold().run();
  const handleItalic = () => editor.chain().focus().toggleItalic().run();
  const handleCode   = () => editor.chain().focus().toggleCode().run();

  // ── Heading picker ──────────────────────────────────────────────────────
  const handleHeadingOpen = () => {
    setHeadingOpen(true);
    onEngaged?.(true);
  };

  const handleHeadingClose = () => {
    setHeadingOpen(false);
    onEngaged?.(false);
  };

  const handleHeading = (level) => {
    editor.chain().focus().setHeading({ level }).run();
    handleHeadingClose();
  };

  const handleParagraph = () => {
    editor.chain().focus().setParagraph().run();
    handleHeadingClose();
  };

  const activeHeadingLevel = HEADING_LEVELS.find((l) =>
    editor.isActive('heading', { level: l })
  );

  return (
    <div className={styles.bubbleMenu}>
      {/* Bold */}
      <Tooltip title="Bold (Ctrl+B)" arrow>
        <IconButton
          size="small"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleBold}
          className={editor.isActive('bold') ? styles.activeBtn : ''}
        >
          <FormatBold fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Italic */}
      <Tooltip title="Italic (Ctrl+I)" arrow>
        <IconButton
          size="small"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleItalic}
          className={editor.isActive('italic') ? styles.activeBtn : ''}
        >
          <FormatItalic fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Inline Code */}
      <Tooltip title="Inline code" arrow>
        <IconButton
          size="small"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleCode}
          className={editor.isActive('code') ? styles.activeBtn : ''}
        >
          <Code fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Link — clicking opens the Dialog owned by RichTextEditor */}
      <Tooltip title="Insert / edit link" arrow>
        <IconButton
          size="small"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onOpenLinkDialog?.()}
          className={editor.isActive('link') ? styles.activeBtn : ''}
        >
          <Link fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Heading picker */}
      <Tooltip title="Heading" arrow>
        <IconButton
          ref={headingAnchor}
          size="small"
          onMouseDown={(e) => e.preventDefault()}
          onClick={headingOpen ? handleHeadingClose : handleHeadingOpen}
          className={activeHeadingLevel ? styles.activeBtn : ''}
        >
          <Title fontSize="small" />
          <span className={styles.headingLabel}>
            {activeHeadingLevel ? `H${activeHeadingLevel}` : 'H'}
          </span>
        </IconButton>
      </Tooltip>

      {/* Heading popover — buttons only, no text input, keep editor focused */}
      <Popover
        disableAutoFocus
        disableEnforceFocus
        disableScrollLock
        open={headingOpen}
        anchorEl={headingAnchor.current}
        onClose={handleHeadingClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { display: 'flex', alignItems: 'center', p: 0.5, gap: 0.25 },
          onMouseDown: (e) => e.stopPropagation(),
        }}
      >
        {HEADING_LEVELS.map((level) => (
          <Tooltip key={level} title={`Heading ${level}`} arrow>
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleHeading(level)}
              className={editor.isActive('heading', { level }) ? styles.activeBtn : ''}
              sx={{ fontWeight: 700, fontSize: '0.75rem', width: 34, height: 34 }}
            >
              H{level}
            </IconButton>
          </Tooltip>
        ))}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
        <Tooltip title="Normal paragraph" arrow>
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleParagraph}
            sx={{ fontSize: '0.75rem', width: 34, height: 34 }}
          >
            ¶
          </IconButton>
        </Tooltip>
      </Popover>
    </div>
  );
}
